import { Box, ColorPreset, useTheme } from "@gocardless/flux-react";
import { AnimatePresence, motion, Transition, Variants } from "framer-motion";

import { HeaderLayout } from "./Headers/HeaderLayout";
import { LayoutProps } from "./types";
import { layoutGutterVStyle } from "./styles";

const SPRING_TRANSITION: Transition = {
  type: "spring",
  stiffness: 150,
  damping: 20,
};

const TRANSITION_VARIANTS: Variants = {
  hidden: { opacity: 0, x: 200, y: 0 },
  enter: { opacity: 1, x: 0, y: 0 },
  exit: { opacity: 1, x: -100, y: 0 },
};

export interface SingleFixedLayoutProps extends LayoutProps {
  /**
   * Allows xs breakpoint to have a full screen view
   */
  xsFullScreenView?: boolean;

  /**
   * Whether to animate page transitions
   */
  animatePageTransitions?: boolean;
}

const MAX_WIDTH_LG = "824px";
const MAX_WIDTH_MD = "760px";
const MAX_WIDTH_SM = "712px";

const ChildrenWrapper = ({
  children,
  animatePageTransitions = false,
}: {
  children: React.ReactNode;
  animatePageTransitions?: boolean;
}) =>
  animatePageTransitions ? (
    <AnimatePresence>
      <motion.div
        initial="hidden"
        animate="enter"
        exit="exit"
        variants={TRANSITION_VARIANTS}
        transition={SPRING_TRANSITION}
      >
        {children}
      </motion.div>
    </AnimatePresence>
  ) : (
    <>{children}</>
  );

export const SingleFixedLayout: React.FC<SingleFixedLayoutProps> = ({
  children,
  header,
  xsFullScreenView,
  animatePageTransitions = false,
}) => {
  const { theme } = useTheme();

  const defaultBg = ColorPreset.BackgroundLight_02;

  const xsBg = xsFullScreenView ? ColorPreset.BackgroundLight_01 : defaultBg;

  return (
    <Box
      layout="flex"
      flexDirection="column"
      bg={[xsBg, defaultBg]}
      minHeight="100dvh"
    >
      {header && (
        <HeaderLayout progressBar={header.progressBar}>
          {header.content}
        </HeaderLayout>
      )}
      <Box
        gutterH={[1.5, null, 2, 4, null]}
        maxWidth={[MAX_WIDTH_SM, null, MAX_WIDTH_MD, MAX_WIDTH_LG]}
        width="100%"
        css={[layoutGutterVStyle(theme), { margin: "0 auto" }]}
        flexGrow={1}
      >
        <ChildrenWrapper animatePageTransitions={animatePageTransitions}>
          {children}
        </ChildrenWrapper>
      </Box>
    </Box>
  );
};
